import { useState, useRef, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Package,
  CreditCard,
  Building2,
  UserCog,
  CalendarCheck,
  Settings,
  LogOut,
  Menu,
  ChevronDown,
  ChevronRight,
  Sun,
  Moon,
  Crown,
  UserPlus,
  BarChart3,
  Calendar,
  TrendingUp,
  UserSearch,
  Dumbbell,
  MessageSquare,
  MessageCircle,
  FileText,
  Activity,
  Wallet,
  Trash2,
  RotateCw,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import { ScrollArea } from "../components/ui/scroll-area";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Breadcrumbs } from "./Breadcrumbs";
import { BrandLogo } from "./BrandLogo";

const navItems = [
  {
    path: "/",
    label: "DASHBOARD",
    icon: LayoutDashboard,
    permission: "view_dashboard",
  },
  {
    label: "MEMBERS",
    path: "/members",
    icon: Users,
    permission: (hp) => hp("view_members") || hp("view_own_members"),
    submenu: [
      {
        path: "/members/overview",
        label: "Overview",
        icon: BarChart3,
        permission: "members_overview",
      },
      {
        path: "/members/new",
        label: "Add Member",
        icon: UserPlus,
        permission: "create_member",
      },
      {
        path: "/members",
        label: "All Members",
        icon: Users,
        permission: (hp) => hp("view_members") || hp("view_own_members"),
      },
      {
        path: "/followups",
        label: "Followups",
        icon: MessageSquare,
        permission: (hp) => hp("view_followups") || hp("manage_followups"),
      },
    ],
  },
  {
    label: "MEMBERSHIPS",
    path: "/memberships",
    icon: CreditCard,
    permission: "view_memberships",
    submenu: [
      {
        path: "/memberships/overview",
        label: "Overview",
        icon: BarChart3,
        permission: "view_memberships",
      },
      {
        path: "/memberships",
        label: "All Memberships",
        icon: CreditCard,
        permission: "view_memberships",
      },
      {
        path: "/memberships/payments",
        label: "Payments",
        icon: Wallet,
        permission: "view_payments",
      },
    ],
  },
  {
    label: "LEADS",
    path: "/leads/all",
    icon: UserSearch,
    permission: "view_leads",
    submenu: [
      {
        path: "/leads",
        label: "Overview",
        icon: BarChart3,
        permission: "view_leads",
      },
      {
        path: "/leads/all",
        label: "All Leads",
        icon: UserSearch,
        permission: "view_leads",
      },
      {
        path: "/leads/followups",
        label: "Followups",
        icon: Calendar,
        permission: "view_leads",
      },
    ],
  },
  {
    label: "WORKOUT",
    path: "/workouts",
    icon: Dumbbell,
    permission: "view_workouts",
    submenu: [
      {
        path: "/workouts/overview",
        label: "Overview",
        icon: BarChart3,
        permission: "view_workouts",
      },
      {
        path: "/workouts",
        label: "All Workouts",
        icon: Dumbbell,
        permission: "view_workouts",
      },
    ],
  },
  {
    path: "/packages",
    label: "PACKAGES",
    icon: Package,
    permission: "view_packages",
  },

  {
    path: "/branches",
    label: "BRANCHES & BIOMETRICS",
    icon: Building2,
    permission: "view_branches",
  },
  { path: "/staff", label: "STAFF", icon: UserCog, permission: "view_staff" },
  {
    path: "/attendance",
    label: "ATTENDANCE",
    icon: CalendarCheck,
    permission: "view_attendance",
  },
  {
    path: "/calendar",
    label: "CALENDAR",
    icon: Calendar,
    permission: "view_calendar",
  },
  {
    path: "/progress-calendar",
    label: "GOAL PROGRESS",
    icon: TrendingUp,
    permission: "view_progress",
  },
  {
    path: "/whatsapp",
    label: "WHATSAPP",
    icon: MessageCircle,
    permission: "view_whatsapp",
  },
  {
    path: "/subscription",
    label: "SUBSCRIPTION",
    icon: Crown,
    permission: "view_subscription",
  },
  {
    path: "/settings",
    label: "SETTINGS",
    icon: Settings,
    permission: "view_settings",
  },
  {
    path: "/deleted-items",
    label: "DELETED ITEMS",
    icon: Trash2,
    ownerOnly: true,
  },
];

export default function Layout({ children }) {
  const {
    user,
    logout,
    currentBranch,
    branches,
    switchBranch,
    organization,
    hasPermission,
  } = useAuth();
  const { theme, setTheme, setColorPreset } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);

  useEffect(() => {
    if (organization?.theme) {
      setTheme(organization.theme);
    }
    if (organization?.colorPreset) {
      setColorPreset(organization.colorPreset);
    }
  }, [organization?.theme, organization?.colorPreset, setTheme, setColorPreset]);

  const desktopScrollRef = useRef(null);

  useEffect(() => {
    const viewport = desktopScrollRef.current?.querySelector(
      "[data-radix-scroll-area-viewport]",
    );
    if (viewport) viewport.scrollTop = 0;

    // Auto-open menu containing current path
    const activeItem = navItems.find(
      (item) => item.submenu && isMenuActive(item.submenu),
    );
    if (activeItem) {
      setOpenMenu(activeItem.label);
    }
  }, [location.pathname]);

  const toggleMenu = (label) =>
    setOpenMenu((prev) => (prev === label ? null : label));

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname === path;
  };

  const isMenuActive = (submenu) =>
    submenu?.some((item) => {
      if (location.pathname === item.path) return true;
      if (item.path === "/members") {
        const pat = /^\/members\/[^/]+(\/(edit))?$/;
        return (
          pat.test(location.pathname) &&
          !["/new", "/overview"].some((s) => location.pathname.includes(s))
        );
      }
      if (item.path === "/memberships") {
        return (
          /^\/memberships\/[^/]+$/.test(location.pathname) &&
          !["/overview", "/payments"].some((s) => location.pathname.includes(s))
        );
      }
      return false;
    });

  const checkPerm = (permission) => {
    if (permission === null || permission === undefined) return true;
    if (typeof permission === "function") return permission(hasPermission);
    return hasPermission(permission);
  };

  const visibleNav = navItems
    .filter((item) => {
      if (item.ownerOnly) return user?.role === "owner";
      return checkPerm(item.permission);
    })
    .map((item) => {
      if (!item.submenu) return item;
      const visibleSub = item.submenu.filter((sub) =>
        checkPerm(sub.permission),
      );
      return { ...item, submenu: visibleSub };
    })
    .filter((item) => !item.submenu || item.submenu.length > 0);

  const NavItem = ({ item, onClick }) => {
    const Icon = item.icon;

    if (item.submenu) {
      const menuActive = isMenuActive(item.submenu);
      const isOpen = openMenu === item.label;
      return (
        <Collapsible open={isOpen} onOpenChange={() => toggleMenu(item.label)}>
          <CollapsibleTrigger asChild>
            <button
              onClick={() => {
                if (item.path) {
                  navigate(item.path);
                }
                if (onClick) {
                  onClick();
                }
              }}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-xs font-heading tracking-wide transition-all duration-200 ${
                menuActive
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary"
              }`}
            >
              <span className="flex items-center gap-3">
                <Icon className="h-4 w-4" />
                {item.label}
              </span>
              <ChevronRight
                className={`h-3.5 w-3.5 transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`}
              />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="pl-4 mt-1 space-y-1">
            {item.submenu.map((subItem) => {
              const SubIcon = subItem.icon;
              const subActive = isActive(subItem.path);
              return (
                <Link
                  key={subItem.path}
                  to={subItem.path}
                  onClick={onClick}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg text-xs transition-all duration-200 ${
                    subActive
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                  }`}
                >
                  <SubIcon className="h-3.5 w-3.5" />
                  {subItem.label}
                </Link>
              );
            })}
          </CollapsibleContent>
        </Collapsible>
      );
    }

    const active = isActive(item.path);
    return (
      <Link
        to={item.path}
        onClick={onClick}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-heading tracking-wide transition-all duration-200 ${
          active
            ? "bg-primary text-primary-foreground shadow-sm"
            : "text-muted-foreground hover:text-foreground hover:bg-secondary"
        }`}
      >
        <Icon className="h-4 w-4" />
        {item.label}
      </Link>
    );
  };

  const SidebarInner = ({ onNavClick }) => (
    <>
      {/* Logo */}
      <div className="px-5 py-4 border-b border-border shrink-0">
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0 space-y-2">
            <h1>
              <BrandLogo />
            </h1>
            <p className="text-[10px] text-muted-foreground font-mono uppercase tracking-widest truncate">
              {organization?.name || "GYM CRM"}
            </p>
          </div>
        </div>
      </div>

      {/* Branch Selector */}
      {branches.length > 0 && (
        <div className="px-4 py-3 border-b border-border shrink-0">
          <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest mb-2 px-1">
            LOCATION
          </p>
          {branches.length === 1 ? (
            <div className="flex items-center gap-2 h-9 px-3 rounded-lg bg-secondary text-xs font-heading uppercase tracking-wide">
              <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="truncate">{branches[0].name}</span>
            </div>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="secondary"
                  className="w-full justify-between h-9 text-xs font-heading uppercase tracking-wide rounded-lg"
                  data-testid="branch-switcher"
                >
                  <span className="truncate">
                    {currentBranch?.name || branches[0]?.name}
                  </span>
                  <ChevronDown className="h-3.5 w-3.5 ml-2 shrink-0 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="start">
                {branches.map((b) => (
                  <DropdownMenuItem
                    key={b.id}
                    onClick={() => switchBranch(b)}
                    className="text-xs"
                    data-testid={`branch-${b.id}`}
                  >
                    {b.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}

      {/* Staff role badge */}
      {user?.role === "staff" && (
        <div className="px-4 py-2 border-b border-border shrink-0">
          <p className="text-[10px] font-mono text-primary/70 uppercase tracking-widest">
            Staff Account — {user.permissions?.length || 0} permissions
          </p>
        </div>
      )}

      {/* Nav */}
      <div className="px-3 py-4">
        <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest mb-3 px-2">
          NAVIGATION
        </p>
        <nav className="space-y-1">
          {visibleNav.map((item) => (
            <NavItem
              key={item.label || item.path}
              item={item}
              onClick={onNavClick}
            />
          ))}
        </nav>
      </div>

      {/* Footer */}
      <div className="border-t border-border shrink-0">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest">
            REFRESH
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-foreground rounded-lg transition-transform hover:rotate-180 duration-500"
            onClick={() => window.location.reload()}
            data-testid="refresh-button"
            title="Refresh App"
          >
            <RotateCw className="h-3.5 w-3.5" />
          </Button>
        </div>
        <div className="p-4">
          <div className="flex items-center gap-3">
            <Avatar className="h-9 w-9 border border-border rounded-xl">
              <AvatarFallback className="bg-secondary text-foreground text-sm font-heading rounded-xl">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-[10px] text-muted-foreground font-mono uppercase tracking-wider">
                {user?.role}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-destructive rounded-lg"
              onClick={() => {
                window.location.href = logout();
              }}
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div
      data-layout-root="true"
      className="overflow-hidden bg-background flex flex-col h-[100dvh] w-full"
    >
      {/* Mobile Header — sticky at top, sits outside the flex-1 scroll area */}
      <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-border bg-card/80 backdrop-blur-xl shrink-0 z-50">
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 rounded-lg"
              data-testid="mobile-menu-button"
            >
              <Menu className="h-8 w-8 !size-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72 border-r border-border">
            <div className="flex flex-col h-full bg-card">
              <ScrollArea className="flex-1">
                <SidebarInner onNavClick={() => setSidebarOpen(false)} />
              </ScrollArea>
            </div>
          </SheetContent>
        </Sheet>
        <h1 className="flex items-center">
          <BrandLogo size="sm" />
        </h1>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-foreground rounded-lg transition-transform hover:rotate-180 duration-500"
            onClick={() => window.location.reload()}
            data-testid="mobile-refresh-button"
            title="Refresh App"
          >
            <RotateCw className="h-4 w-4" />
          </Button>
          <Avatar className="h-8 w-8 border border-border rounded-xl">
            <AvatarFallback className="bg-secondary text-foreground text-xs font-heading rounded-xl">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </AvatarFallback>
          </Avatar>
        </div>
      </div>

      {/* Body row — sidebar + main, fills remaining height */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Desktop sidebar — fixed height, only its ScrollArea scrolls internally */}
        <div className="hidden lg:flex flex-col w-64 shrink-0 border-r border-border bg-card h-full">
          <ScrollArea className="flex-1" ref={desktopScrollRef}>
            <SidebarInner onNavClick={null} />
          </ScrollArea>
        </div>

        {/* Main content — this is the ONLY thing that scrolls */}
        <main className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden h-full">
          <div className="p-1 md:pl-4 lg:p-5 h-full flex min-h-0 flex flex-col">
            <Breadcrumbs className="hidden md:flex" />
            <div className="animate-fade-in-up flex-1 min-h-0 flex flex-col">
              {children}
            </div>
          </div>
        </main>

        {/* <main className={`flex-1 min-w-0 ${noScroll ? "h-full overflow-hidden flex flex-col" : "overflow-y-auto overflow-x-hidden"}`}>
          <div className={`${noScroll
            ? "p-4 md:p-6 lg:p-8 pb-0 md:pb-0 lg:pb-0 h-full min-h-0 flex flex-col"
            : "p-1 md:pl-4 lg:p-8"
            }`}>
            <Breadcrumbs />
            <div className={`animate-fade-in-up ${noScroll ? "flex-1 min-h-0 flex flex-col" : ""}`}>{children}</div>
          </div>
        </main> */}
      </div>
    </div>
  );
}
